// src/pages/Locations.jsx
import React, { useMemo, useState } from "react";
import useGuidesList from "../hooks/useGuidesList";
import Container from "../components/Container";
import GuideDownload from "../components/GuideDownload";
import DownloadsBadge from "../components/DownloadsBadge";
import useDownloadCount from "../hooks/useDownloadCount";
import { nf0 } from "../utils/downloadsCounter";

const num = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;

//function CountBadge({ item }) {
//  const base = num(item.base_downloads ?? item.downloads_base ?? item.base ?? 0);
//  const startDateISO = item.downloads_start || item.date || "2025-01-01";
//   const { total } = useDownloadCount({ slug: item.slug, base, startDateISO });
// return <span className="text-xs text-slate-600">{nf0.format(total)} downloads</span>;}

const clean = (s) => (s || "").toLowerCase().trim();
const isLocation = (a) => {
  const c = clean(a.category);
  return c === "locations" || c === "location" || c.includes("location");
};
const getTags = (a) =>
  (a.tags || a.Tags || "")
    .split(",")
    .map((t) => clean(t))
    .filter(Boolean);

export default function Locations() {
  const all = useGuidesList();
  const items = all.filter(isLocation);

  const [selectedTags, setSelectedTags] = useState([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("Newest");

  const filtered = useMemo(() => {
    const q = clean(query);
    let arr = items.filter((a) => {
      const tags = getTags(a);
      const tagMatch =
        selectedTags.length === 0 ||
        selectedTags.every((t) => tags.includes(t));
      const qMatch =
        !q || clean(a.title).includes(q) || clean(a.excerpt).includes(q);
      return tagMatch && qMatch;
    });

    return arr.sort((a, b) =>
      sort === "Newest"
        ? new Date(b.date) - new Date(a.date)
        : new Date(a.date) - new Date(b.date)
    );
  }, [items, query, selectedTags, sort]);

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img
          src="/content/images/guide-hero.jpg"
          alt="Locations header"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-white/30 to-white" />
        <Container className="relative py-16 sm:py-20">
          <h1 className="text-4xl font-bold text-[#003366]">Locations</h1>
          <p className="mt-3 text-[#003366] max-w-2xl">
            Location spotlights and city deep-dives.
          </p>
        </Container>
      </section>

      {/* Controls */}
      <section>
        <Container className="py-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search locations…"
              className="w-full sm:w-80 rounded-xl border border-slate-300 px-3 py-2"
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-2"
            >
              <option>Newest</option>
              <option>Oldest</option>
            </select>
          </div>
        </Container>
      </section>

      {/* Results */}
      <section>
        <Container className="pb-16">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-[#003366]">
              No matching locations yet.
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a) => (
                <article
                  key={a.id || a.slug}
                  className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden"
                >
                  {/* image + overlay badge */}
                  <div className="relative">
                    
                    <img src={a.image} alt="" className="h-48 w-full object-cover" />
                  </div>

                  <div className="p-6">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#003366]">
                      {a.category}
                    </div>
                    <h3 className="mt-1 text-lg font-semibold text-[#003366]">
                      {a.title}
                    </h3>
                    <p className="mt-2 text-[#003366]">{a.excerpt}</p>

                    <div className="mt-4 flex items-center justify-between gap-3 text-sm text-[#003366]">
                      
                      <div className="flex items-center gap-3">
                        {/* Same link rule as Guides */}
                        <a
                          href={
                            (a.category || "").toLowerCase() === "locations"
                              ? `#/guide/${a.slug}`
                              : `#/article/${a.slug}`
                          }
                          className="font-semibold text-[#003366]"
                        >
                          Read more →
                        </a>

                        <GuideDownload
                          guideId={a.id || a.slug || a.guid}
                          guideTitle={a.title || a.name || a.heading || a.slug}
                          slug={a.slug}
                          item={a}
                          label="Download PDF"
                        />

                        
                      </div>
                    </div>

                    {getTags(a).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {getTags(a).map((t) => (
                          <span
                            key={t}
                            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-[#003366]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </Container>
      </section>
    </main>
  );
}
